import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import qs from 'qs';

import TableHeaderCheckboxAll from '../../../../components/Table/CheckAll';
import DropdownRequest from '../../../../components/Dropdown/Request';

import { getTicketsStaffCountsAction, getTicketsStatusesCountsAction, clearTicketsFilters } from '../../../../redux/actions/tickets';
import { getRootStateSelector } from '../../../../redux/selectors/root';
import { getTicketsLevelCounts } from '../../../../services/endpoints/tickets';

const TableHead = ({
  pageFilters,
  values,
  staff,
  staffFetching,
  statuses,
  config,
  handleSelect,
  onChangeCheck,
  accessMassActions,
  onSelectStatuses,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const queryString = qs.parse(location.search.slice(1));
  const availableLevel = !!config?.user_levels;
  const { user_levels } = useSelector((state) => getRootStateSelector(state));
  const { darkMode } = useSelector((state) => state.general);

  const [headerFilters, setHeaderFilters] = useState({
    levels: [{ id: null, count: null, title: 'All' }, ...user_levels],
  });

  const selectedStatuses = useMemo(
    () => {
      if (statuses?.length) {
        return statuses?.filter((status) => !!pageFilters?.status?.map((s) => String(s))?.includes(String(status.id)));
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, pageFilters?.status, statuses],
  );
  const filters = useMemo(
    () => ({
      statuses: statuses?.map((status) => ({
        ...status,
        label: status.title,
        count: status.count,
      })),
    }),
    [statuses],
  );

  const getLevelsFilter = async () => {
    try {
      const response = await getTicketsLevelCounts(queryString);
      let allCount = 0;
      const levels = response?.data?.['user-levels']?.map((status) => {
        allCount += status.count;
        return {
          ...status,
          title: `${status.title} ${status.count || status.count === 0 ? `(${status.count})` : ''}`,
        };
      });
      setHeaderFilters((prevState) => ({
        ...prevState,
        levels: [{ id: null, count: null, title: allCount ? `All (${allCount})` : 'All' }, ...levels],
      }));
    } catch (error) {
      return error;
    }
  };

  return (
    <thead>
      <tr className="tickets__head">
        {config?.mass_actions ? (
          <TableHeaderCheckboxAll
            handleSelect={handleSelect}
            transKey="Table.MassCounterTickets"
            transValue="{{count}} tickets selected"
            values={values}
            onChangeCheck={onChangeCheck}
            accessMassActions={accessMassActions}
          />
        ) : (
          <th> </th>
        )}
        <th className="p-l tickets__id">ID</th>
        <th className="tickets__user">User</th>
        {availableLevel && (
          <th className="tickets-level">
            <DropdownRequest
              title="Level"
              items={headerFilters.levels}
              queryRequest="level"
              onRequestFunc={() => getLevelsFilter()}
              clearData={() => dispatch(clearTicketsFilters())}
              darkMode={darkMode}
              fetchItems
              pullRight
            />
          </th>
        )}
        <th className="tickets-subject">Subject</th>
        <th className="tickets__status tickets__status-multiply">
          <DropdownRequest
            type="select"
            title="Status"
            items={filters?.statuses}
            value={selectedStatuses}
            queryRequest="status"
            onRequestFunc={() => dispatch(getTicketsStatusesCountsAction(queryString))}
            clearData={() => true}
            withServicesId={false}
            selectSearch={false}
            onChangeSelect={onSelectStatuses}
            darkMode={darkMode}
          />
        </th>
        <th className="tickets__status tickets-assignee tickets-date">
          <DropdownRequest
            title="Assignee"
            items={staff}
            queryRequest="assignee"
            onRequestFunc={() => dispatch(getTicketsStaffCountsAction(queryString))}
            clearData={() => dispatch(clearTicketsFilters())}
            fetchItems={staffFetching}
            darkMode={darkMode}
            pullRight
          />
        </th>
        <th className="tickets-date">Created</th>
        <th className="tickets-date">Last update</th>
      </tr>
    </thead>
  );
};

TableHead.propTypes = {
  pageFilters: PropTypes.object,
  values: PropTypes.array,
  staff: PropTypes.array,
  staffFetching: PropTypes.bool,
  statuses: PropTypes.array,
  config: PropTypes.object,
  handleSelect: PropTypes.func,
  onChangeCheck: PropTypes.func,
  accessMassActions: PropTypes.array,
  onSelectStatuses: PropTypes.func,
};

export default TableHead;
